import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Trophy, Medal, Flag, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Permanent per-round results panel. Rendered underneath the live standings
 * on the Leaderboard page. One accordion item per captured official round.
 *
 * Props:
 *   league          - "racing" or "moto"
 *   currentUserId   - highlight current player's row
 *   accentColor     - tailwind color name for accents
 */
export default function RoundResultsAccordion({ league, currentUserId, accentColor = 'primary' }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/round-results?league=${league}`, { withCredentials: true })
      .then((res) => {
        if (!alive) return;
        // Only show OFFICIAL captured rounds; hide test rounds
        const official = (res.data || []).filter(s => s.round_type !== 'test');
        official.sort((a, b) => b.round_number - a.round_number); // newest first
        setSnapshots(official);
      })
      .catch(() => setSnapshots([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [league]);

  const rankIcon = (rank) => {
    if (rank === 1) return <Trophy size={16} className="text-yellow-400" />;
    if (rank === 2) return <Medal size={16} className="text-gray-400" />;
    if (rank === 3) return <Medal size={16} className="text-orange-400" />;
    return <span className="text-zinc-500 font-mono text-sm">#{rank}</span>;
  };

  if (loading) {
    return <p className="text-zinc-500 text-center py-8" data-testid="round-results-loading">Loading round results…</p>;
  }

  if (!snapshots.length) {
    return (
      <div className="bg-card border border-zinc-800 p-8 text-center" data-testid="round-results-empty">
        <Flag size={32} className="mx-auto text-zinc-600 mb-3" />
        <p className="text-zinc-400 font-heading text-lg">No captured rounds yet</p>
        <p className="text-zinc-500 text-sm mt-1">
          Once a round finishes and is captured by admin, results will appear here permanently.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="round-results-accordion">
      <div className="flex items-center justify-between mb-4">
        <h2 className={`font-heading font-bold text-2xl`}>
          <Flag className={`inline text-${accentColor} mr-2 -mt-1`} size={22} />
          ROUND RESULTS
        </h2>
      </div>

      <Accordion type="multiple" defaultValue={[`round-${snapshots[0].round_number}`]} className="space-y-2">
        {snapshots.map((snap) => (
          <RoundItem
            key={snap.snapshot_id || snap.round_number}
            snap={snap}
            currentUserId={currentUserId}
            accentColor={accentColor}
            rankIcon={rankIcon}
          />
        ))}
      </Accordion>
    </div>
  );
}

function RoundItem({ snap, currentUserId, accentColor, rankIcon }) {
  const sortedScores = useMemo(() => {
    const arr = [...(snap.player_scores || [])];
    arr.sort((a, b) => (b.round_points || 0) - (a.round_points || 0));
    return arr;
  }, [snap.player_scores]);

  const capturedAt = snap.captured_at ? new Date(snap.captured_at).toLocaleString() : '';

  return (
    <AccordionItem
      value={`round-${snap.round_number}`}
      className="bg-card border border-zinc-800"
    >
      <AccordionTrigger
        className="px-6 py-4 hover:no-underline"
        data-testid={`round-results-trigger-${snap.round_number}`}
      >
        <div className="flex items-center justify-between w-full pr-4">
          <div className="text-left">
            <span className={`bg-${accentColor} text-black px-2 py-0.5 font-heading font-bold text-xs`}>
              ROUND {snap.round_number}
            </span>
            <span className="ml-3 font-heading font-bold text-lg">{snap.round_name}</span>
          </div>
          <div className="text-right text-xs text-zinc-500 hidden sm:block">
            {sortedScores.length} players · {capturedAt}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-3 sm:px-6 pb-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-zinc-500 text-xs">
                <th className="text-left p-2 w-12">#</th>
                <th className="text-left p-2">Player</th>
                <th className="text-right p-2">Round pts</th>
                <th className="text-right p-2 hidden sm:table-cell">Boosts</th>
              </tr>
            </thead>
            <tbody>
              {sortedScores.map((p, idx) => {
                const isCurrent = p.user_id === currentUserId;
                return (
                  <tr
                    key={p.user_id}
                    className={`border-b border-zinc-900 ${isCurrent ? `bg-${accentColor}/10` : 'hover:bg-zinc-900/50'}`}
                    data-testid={`round-${snap.round_number}-row-${idx}`}
                  >
                    <td className="p-2">{rankIcon(idx + 1)}</td>
                    <td className="p-2">
                      <span className="font-bold text-white">{p.name}</span>
                      {isCurrent && (
                        <span className={`ml-2 text-[10px] text-${accentColor} font-mono`}>(YOU)</span>
                      )}
                      {p.team_name && (
                        <p className={`text-[10px] text-${accentColor} font-heading font-bold uppercase`}>{p.team_name}</p>
                      )}
                    </td>
                    <td className={`p-2 text-right font-mono font-bold text-${accentColor}`}>
                      {Math.round(p.round_points || 0)}
                    </td>
                    <td className="p-2 text-right hidden sm:table-cell">
                      {(p.boost_points || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 text-yellow-400 text-xs">
                          <Zap size={10} className="fill-yellow-400" />
                          +{Math.round(p.boost_points)}
                        </span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
